import { createAsyncThunk } from '@reduxjs/toolkit';
import type { IEvent, ApiError, EventDetailsResponse } from '../api/events.types';
import { axiosInstance } from '@/shared/api/instance';

export const fetchEvents = createAsyncThunk<IEvent[], void, { rejectValue: ApiError }>(
  'events/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get<IEvent[]>('/app/events/index');
      return data;
    } catch (error: any) {
      const defaultError = {
        code: 500,
        message: 'Unknown error occurred',
      };

      if (!error.response) {
        return rejectWithValue({
          code: 503,
          message: 'Network error - server unavailable',
        });
      }

      return rejectWithValue({
        code: error.response.status || defaultError.code,
        message: error.response.data?.message || defaultError.message,
      });
    }
  },
);

export const fetchEventDetails = createAsyncThunk<
  EventDetailsResponse,
  string,
  { rejectValue: ApiError }
>('events/fetchEventDetails', async (id, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.get<EventDetailsResponse>(`/app/events/view`, {
      params: { id },
    });

    return data;
  } catch (error: any) {
    const defaultError = {
      code: 500,
      message: 'Unknown error occurred',
    };

    if (!error.response) {
      return rejectWithValue({
        code: 503,
        message: 'Network error - server unavailable',
      });
    }

    return rejectWithValue({
      code: error.response.status || defaultError.code,
      message: error.response.data?.message || defaultError.message,
    });
  }
});

export const buyTicketsByGold = createAsyncThunk<
  { id: string },
  { eventId: string; ticketCount: number },
  { rejectValue: ApiError }
>('events/buyTicketsByGold', async ({ eventId, ticketCount }, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.get('/app/purchases/buy-tickets-by-gold', {
      params: { eventId, ticketCount },
    });
    return data;
  } catch (error: any) {
    const defaultError = {
      code: 500,
      message: 'Unknown error occurred',
      description: 'Неизвестная ошибка',
    };

    if (!error.response) {
      return rejectWithValue({
        code: 503,
        message: 'Network error - server unavailable',
        description: 'Сервер недоступен',
      });
    }

    return rejectWithValue({
      code: error.response.status || defaultError.code,
      message: error.response.data?.message || defaultError.message,
      description: error.response.data?.description || defaultError.description,
    });
  }
});
export const getEventPaymentUrl = createAsyncThunk<
  { url: string; id: string },
  { eventId: string; ticketCount: number; discountId: string | null },
  { rejectValue: ApiError }
>(
  'events/getEventPaymentUrl',
  async ({ eventId, ticketCount, discountId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get<{
        url: string;
        id: string;
      }>('/app/purchases/get-event-payment-url', {
        params: { eventId, ticketCount, discountId },
      });
      return data;
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error occurred',
        description: error.response?.data?.description || 'Ошибка получения ссылки',
      });
    }
  },
);

export const checkEventPaymentStatus = createAsyncThunk<
  { eventId: string; ticketsCount: number; isConfirmed: boolean },
  string,
  { rejectValue: ApiError }
>('events/checkPaymentStatus', async (paymentId, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.get<{
      eventId: string;
      ticketsCount: number;
      isConfirmed: boolean;
    }>('/app/validate-payments/event', {
      params: { id: paymentId },
      timeout: 25000,
    });
    return data;
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Error checking payment status',
      description: error.response?.data?.description || 'Ошибка получения ссылки',
    });
  }
});
